import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class PlayerProService {
  static readonly PLAYER_VERSION_DATE_REGEX = /\d{4}\.\d{2}\.\d{2}\.*/;
  static readonly SCREENSHOT_PLAYER_VERSION = '2017.01.10.17.33';
  static readonly DISPLAY_CONTROL_PLAYER_VERSION = '2018.01.15.16.31';
  static readonly CHROMEOS_PLAYER_VERSION = '2018.07.20.10229';
  static readonly CHROMEOS_SCREENSHOT_PLAYER_VERSION = '2018.08.17.8388';
  static readonly PROMETHEAN_OPS_PRODUCT_CODES = ['OPS-A1', 'OPS-A2'];
  static readonly AMAZON_SIGNAGE_STICK_PRODUCT_CODE = 'AFKLNK';
  static readonly SIGNAGE_STICK_OTA_UPDATE_MIN_PLAYER_VERSION = '11.5.5';

  static readonly COMMON_APP_ID_BASE = 'com.risevision.androidplayer';
  static readonly NANOLUMENS_APP_ID_BASE = `${PlayerProService.COMMON_APP_ID_BASE}.apkNanolumens`;
  static readonly PHILIPS_APP_ID_BASE = `${PlayerProService.COMMON_APP_ID_BASE}.apkPhilips`;
  static readonly PROMETHEAN_D_SERIES_APP_ID_BASE = `${PlayerProService.COMMON_APP_ID_BASE}.apkPrometheanDSeries`;
  static readonly AVOCOR_B_SERIES_APP_ID_BASE = `${PlayerProService.COMMON_APP_ID_BASE}.apkAvocorBSeries`;

  private _compareVersion(minimumVersion, currentVersion) {
    return (
      PlayerProService.PLAYER_VERSION_DATE_REGEX.test(currentVersion) &&
      currentVersion >= minimumVersion
    );
  }

  private _compareNumericVersion(
    minimumVersion: string,
    currentVersion?: string,
  ): boolean {
    if (!currentVersion) {
      return false;
    }
    const parse = (version: string): number[] | null => {
      const segments = String(version).split('.');
      if (!segments.every((segment) => /^\d+$/.test(segment))) {
        return null;
      }
      return segments.map((segment) => parseInt(segment, 10));
    };

    const minimumParts = parse(minimumVersion);
    const currentParts = parse(currentVersion);
    if (!minimumParts || !currentParts) {
      return false;
    }

    const length = Math.max(minimumParts.length, currentParts.length);

    for (let i = 0; i < length; i++) {
      const minimumPart = minimumParts[i] || 0;
      const currentPart = currentParts[i] || 0;
      if (currentPart !== minimumPart) {
        return currentPart > minimumPart;
      }
    }

    return true;
  }

  isCROSLegacy(display?) {
    const os = ((display && display.os) || '').toLowerCase();
    const chromeOSPwa = this.isChromeOSPwaPlayer(display);

    return (
      !chromeOSPwa &&
      os.indexOf('cros') !== -1 &&
      os.indexOf('microsoft') === -1
    );
  }

  isOldElectronPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      display.playerName.indexOf('RisePlayerElectron') !== -1
    );
  }

  isChromeOSPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      display.playerName.indexOf('RisePlayer') !== -1 &&
      !this.isOldElectronPlayer(display) &&
      this._compareVersion(
        PlayerProService.CHROMEOS_PLAYER_VERSION,
        display.playerVersion,
      )
    );
  }

  isWebPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      display.playerName.indexOf('Web Player') !== -1
    );
  }

  isAndroidPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      display.playerName.indexOf('Android Player') !== -1
    );
  }

  isFireOSPlayer(display?) {
    return (
      this.isAndroidPlayer(display) && display.buildType === 'amazon-store'
    );
  }

  isAmazonSignageStick(display?) {
    if (!this.isAndroidPlayer(display)) {
      return false;
    }
    return !!(
      display &&
      display.productName &&
      this.isFireOSPlayer(display) &&
      PlayerProService.AMAZON_SIGNAGE_STICK_PRODUCT_CODE === display.productName
    );
  }

  isChromeOSAppPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      display.playerName.indexOf('chromeos-app-player') !== -1
    );
  }

  isChromeOSPwaPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      (display.playerName.indexOf('Chrome OS Player') !== -1 ||
        display.playerName.indexOf('Chrome OS PWA Player') !== -1)
    );
  }

  isAppleTVPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      display.playerName.indexOf('Apple TV Player') !== -1
    );
  }

  isLGWebOSPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      display.playerName.indexOf('LGWebOS Player') !== -1
    );
  }

  isElectronPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      (display.playerName.indexOf('Windows Player') !== -1 ||
        display.playerName.indexOf('Electron Player') !== -1)
    );
  }

  isWindowsPlayer(display?) {
    return !!(
      display &&
      this.isElectronPlayer(display) &&
      display.os &&
      display.os.indexOf('Windows') !== -1
    );
  }

  isChromecastGoogleTVProduct(display?) {
    return !!(
      display &&
      display.productName &&
      display.productName.startsWith('Chromecast')
    );
  }

  isGiadaRecommendedProduct(display?) {
    if (display) {
      return (
        display.productName === 'jhs558' || display.productName === 'DN76V1'
      );
    }
    return false;
  }

  isPrometheanOps(display?) {
    if (!this.isAndroidPlayer(display)) {
      return false;
    }

    return !!(
      display &&
      display.productName &&
      PlayerProService.PROMETHEAN_OPS_PRODUCT_CODES.includes(
        display.productName,
      )
    );
  }

  isPrometheanDSeries(display?) {
    return !!(
      display &&
      display.appId &&
      display.appId.startsWith(PlayerProService.PROMETHEAN_D_SERIES_APP_ID_BASE)
    );
  }

  isNanolumensDisplay(display?) {
    return !!(
      display &&
      display.appId &&
      display.appId.startsWith(PlayerProService.NANOLUMENS_APP_ID_BASE)
    );
  }

  isPhilipsDisplay(display?) {
    return !!(
      display &&
      display.appId &&
      display.appId.startsWith(PlayerProService.PHILIPS_APP_ID_BASE)
    );
  }

  isScreenshotCompatiblePlayer(display?) {
    const oldElectronSupported =
      this.isOldElectronPlayer(display) &&
      this._compareVersion(
        PlayerProService.SCREENSHOT_PLAYER_VERSION,
        display.playerVersion,
      );
    const chromeOSSupported =
      this.isChromeOSPlayer(display) &&
      this._compareVersion(
        PlayerProService.CHROMEOS_SCREENSHOT_PLAYER_VERSION,
        display.playerVersion,
      );
    const androidSupported = this.isAndroidPlayer(display);
    const chromeOSAppSupported = this.isChromeOSAppPlayer(display);
    const chromeOSPwaSupported = this.isChromeOSPwaPlayer(display);
    const electronSupported = this.isElectronPlayer(display);
    const brightSignSupported = this.isBrightSignPlayer(display);
    const lgWebOSSupported = this.isLGWebOSPlayer(display);

    return (
      oldElectronSupported ||
      chromeOSSupported ||
      chromeOSAppSupported ||
      chromeOSPwaSupported ||
      androidSupported ||
      electronSupported ||
      brightSignSupported ||
      lgWebOSSupported
    );
  }

  isDeviceOrientationCompatiblePlayer(display?) {
    return !!display && !this.isAppleTVPlayer(display);
  }

  isDisplayControlCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    const oldElectronSupported =
      this.isOldElectronPlayer(display) &&
      this._compareVersion(
        PlayerProService.DISPLAY_CONTROL_PLAYER_VERSION,
        display.playerVersion,
      );
    const electronSupported = this.isElectronPlayer(display);
    const androidSupported =
      this.isAndroidPlayer(display) &&
      (this.isGiadaRecommendedProduct(display) ||
        this.isAvocorRSeriesDisplay(display) ||
        this.isAvocorKSeriesDisplay(display) ||
        this.isAvocorBSeriesDisplay(display) ||
        this.isPrometheanDSeries(display) ||
        this.isAmazonSignageStick(display) ||
        this.isPrometheanOps(display) ||
        this.isPhilipsDisplay(display));

    const brightSignSupported = this.isBrightSignPlayer(display);
    const chromeOSPwaSupported = this.isChromeOSPwaPlayer(display);
    const lgWebOSSupported = this.isLGWebOSPlayer(display);

    return (
      (oldElectronSupported ||
        electronSupported ||
        androidSupported ||
        brightSignSupported ||
        chromeOSPwaSupported ||
        lgWebOSSupported) &&
      display.playerProAuthorized
    );
  }

  isRS232DisplayControlCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    const windowsSupported = this.isWindowsPlayer(display);
    const androidSupported = this.isGiadaRecommendedProduct(display);
    const chromeOSPwaSupported = this.isChromeOSPwaPlayer(display);

    return (
      (windowsSupported || androidSupported || chromeOSPwaSupported) &&
      display.playerProAuthorized
    );
  }

  isRebootCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    // Cannot reboot Linux/Windows/Mac PackagedApp players
    if (display.playerName === 'RisePlayerPackagedApp') {
      return false;
    }

    const chromeOSSupported =
      this.isCROSLegacy(display) ||
      this.isChromeOSPlayer(display) ||
      this.isChromeOSAppPlayer(display) ||
      this.isChromeOSPwaPlayer(display);
    const electronSupported = this.isElectronPlayer(display);
    const androidSupported =
      this.isGiadaRecommendedProduct(display) ||
      this.isAvocorRSeriesDisplay(display) ||
      this.isAvocorKSeriesDisplay(display) ||
      this.isAvocorBSeriesDisplay(display) ||
      this.isAmazonSignageStick(display) ||
      this.isPhilipsDisplay(display) ||
      display.productName === 'X10-Mini';
    const brightSignSupported = this.isBrightSignPlayer(display);
    const lgWebOSSupported = this.isLGWebOSPlayer(display);

    return (
      chromeOSSupported ||
      electronSupported ||
      androidSupported ||
      brightSignSupported ||
      lgWebOSSupported
    );
  }

  isRestartCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    if (this.isAppleTVPlayer(display)) {
      return false;
    }
    if (this.isBrightSignPlayer(display)) {
      return false;
    }

    return true;
  }

  isFirmwareUpdateCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    // Firmware updates are only supported for LG WebOS Player
    return this.isLGWebOSPlayer(display);
  }

  isOTAUpdateCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    // OTA updates are only supported for Signage Stick devices
    return (
      this.isAmazonSignageStick(display) &&
      this._compareNumericVersion(
        PlayerProService.SIGNAGE_STICK_OTA_UPDATE_MIN_PLAYER_VERSION,
        display.playerVersion,
      )
    );
  }

  // Check if firmware update is available for a display
  // This could be used to show different UI states (update available, up to date, etc.)
  isFirmwareUpdateAvailable(display?) {
    if (!display) {
      return false;
    }

    // Only LG WebOS Player displays support firmware updates
    if (!this.isLGWebOSPlayer(display)) {
      return false;
    }

    if (!this.isFirmwareUpdateCompatiblePlayer(display)) {
      return false;
    }

    // For now, assume firmware updates are always available for compatible devices
    // In the future, this could check the display's firmwareUpdateStatus field
    // to determine if an update is available, pending, or not needed
    return true;
  }

  isRebootOrRestartDailyCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    if (this.isAvocorDisplay(display)) {
      return false;
    }

    if (this.isAppleTVPlayer(display)) {
      return false;
    }

    return true;
  }

  isScreenSharingCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    if (this.isAppleTVPlayer(display)) {
      return false;
    }

    // Hide screen sharing config for LG WebOS 4.x devices (case-insensitive)
    if (
      this.isLGWebOSPlayer(display) &&
      display.os?.toLowerCase().includes('webos 4')
    ) {
      return false;
    }

    return true;
  }

  isAvocorDisplay(display?) {
    return !!display && display.buildType === 'avocor';
  }

  isAutostartFlagEnabledPlayer(display?) {
    const androidSupported =
      this.isAndroidPlayer(display) &&
      !this.isAvocorDisplay(display) &&
      !this.isNanolumensDisplay(display) &&
      !this.isPhilipsDisplay(display);
    const windowsSupported = this.isMultiUserWindowsInstall(display);

    return androidSupported || windowsSupported;
  }

  isScreensaverCompatiblePlayer(display?) {
    return (
      this.isMultiUserWindowsInstall(display) || this.isPrometheanOps(display)
    );
  }

  isVolumeControlCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    return this.isChromeOSPwaPlayer(display) || this.isLGWebOSPlayer(display);
  }

  isScreenPowerOnCompatiblePlayer(display?) {
    return this.isDisplayControlCompatiblePlayer(display);
  }

  isKeyOperationModeDeviceCompatiblePlayer(display?) {
    if (!display) {
      return false;
    }

    return this.isLGWebOSPlayer(display);
  }

  isBrightSignPlayer(display?) {
    return !!(
      display &&
      display.playerName &&
      display.playerName.indexOf('BrightSign') !== -1
    );
  }

  isAvocorRSeriesDisplay(display?) {
    return !!(
      display && ['smith', 'AVR-4310', 'AVR-5510'].includes(display.productName)
    );
  }

  isClassroomAlertsCompatiblePlayer(display?) {
    return (
      this.isAndroidPlayer(display) || this.isMultiUserWindowsInstall(display)
    );
  }

  isMultiUserWindowsInstall(display?) {
    return !!(
      this.isElectronPlayer(display) &&
      display.buildType &&
      display.buildType.indexOf('multi') !== -1
    );
  }

  isAvocorKSeriesDisplay(display?) {
    return !!(
      display &&
      ['AVK-5510', 'AVK-6510', 'AVK-7510', 'AVK-8510', 'AVK-9810'].includes(
        display.productName,
      )
    );
  }

  isAvocorBSeriesDisplay(display?) {
    return !!(
      display &&
      display.appId &&
      display.appId.startsWith(PlayerProService.AVOCOR_B_SERIES_APP_ID_BASE)
    );
  }
}
